import type { NextApiRequest, NextApiResponse } from 'next';
import { Session, SessionRecord } from 'next-session/lib/types';
import {
  get,
  isArray,
  isDate,
  isEmpty,
  isNull,
  isObject,
  set,
  unset,
} from 'lodash';

import { getSession } from '../../services/session.service';

// export const config = {
//   api: {
//     externalResolver: true,
//   },
// };

function sessionUpdateRecursive(
  session: Record<string, unknown>,
  key: string,
  item: unknown,
): void {
  if (
    isObject(item) &&
    !isArray(item) &&
    !isDate(item) &&
    !isEmpty(item) &&
    !isNull(item)
  ) {
    return Object.keys(item).forEach((subKey) =>
      sessionUpdateRecursive(session, `${key}.${subKey}`, get(item, subKey)),
    );
  }

  if (isArray(item) && !isEmpty(item)) {
    return item.forEach((value, index) =>
      sessionUpdateRecursive(session, `${key}.${index}`, value),
    );
  }

  if (item === '$unset') {
    unset(session, key);
    return;
  }

  set(session, key, item);
}

function clearNullValuesFromArray(
  session: Record<string, unknown>,
  parentKey: string,
): void {
  const parentArray = parentKey.split('.');
  parentArray.pop();
  const key = parentArray.join('.');
  if (isEmpty(key)) return;
  const item = get(session, key);

  if (
    isArray(item) &&
    !isEmpty(item) &&
    JSON.stringify(item).includes('null')
  ) {
    const newArray = item.filter((value: unknown) => !isNull(value));

    newArray.forEach((value) => {
      if (
        isObject(value) &&
        !isArray(value) &&
        !isDate(value) &&
        !isEmpty(value) &&
        !isNull(value)
      ) {
        Object.keys(value).forEach((subKey) =>
          clearNullValuesFromArray(session, `${parentKey}.${subKey}`),
        );
      }

      if (isArray(value) && !isEmpty(value)) {
        value.forEach((_, index) =>
          clearNullValuesFromArray(session, `${parentKey}.${index}`),
        );
      }
    });

    set(session, key, newArray);
  }
}

async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
): Promise<Session<SessionRecord>> {
  const session = await getSession(req, res);

  Object.keys(req.body).forEach((key) => {
    sessionUpdateRecursive(session, key, get(req.body, key));
    clearNullValuesFromArray(session, key);
  });

  return session;
}

handler.$methods = ['PUT'];

export default handler;
